import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import useTranslation from '../hooks/useTranslation';

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const { language } = useLanguage();
  const { t } = useTranslation(language);

  return (
    <footer className="bg-gray-900/95 backdrop-blur-md border-t-2 border-gray-800 text-white relative z-10">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Section Logo et Description */}
          <div className="col-span-1">
            <Link to="/" className="flex items-center mb-4 hover:opacity-80 transition-opacity">
              <img 
                src="/images/logo.png" 
                alt="OpSecours" 
                className="h-8 w-8 mr-3" 
              />
              <span className="text-xl font-bold text-gradient">OpSecours</span>
            </Link>
            <p className="text-gray-400 text-sm leading-relaxed mb-4">
              {t('footer.description')}
            </p>
            <div className="flex space-x-4">
              <a 
                href="https://discord.gg/7wVa8EkvnP" 
                className="text-gray-400 hover:text-primary-400 transition-colors duration-200"
                target="_blank"
                rel="noopener noreferrer"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Section Support */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-gradient">{t('footer.support')}</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="text-gray-400 hover:text-primary-400 transition-colors">
                  {t('footer.home') || 'Accueil'}
                </Link>
              </li>
              <li>
                <Link to="/support/aide" className="text-gray-400 hover:text-primary-400 transition-colors">
                  {t('footer.help') || 'Aide'}
                </Link>
              </li>
              <li>
                <Link to="/support/faq" className="text-gray-400 hover:text-primary-400 transition-colors">
                  FAQ
                </Link>
              </li>
              <li>
                <Link to="/support/contact" className="text-gray-400 hover:text-primary-400 transition-colors">
                  {t('footer.contact') || 'Contact'}
                </Link>
              </li>
              <li>
                <Link to="/support/changelog" className="text-gray-400 hover:text-primary-400 transition-colors">
                  {t('footer.changelog') || 'Changelog'}
                </Link>
              </li>
            </ul>
          </div>

          {/* Section Légale */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-gradient">{t('footer.legal')}</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/legal/mentions-legales" className="text-gray-400 hover:text-primary-400 transition-colors">
                  {t('footer.legalNotice') || 'Mentions légales'}
                </Link>
              </li>
              <li>
                <Link to="/legal/politique-confidentialite" className="text-gray-400 hover:text-primary-400 transition-colors">
                  {t('footer.privacyPolicy') || 'Politique de confidentialité'}
                </Link>
              </li>
              <li>
                <Link to="/legal/conditions-utilisation" className="text-gray-400 hover:text-primary-400 transition-colors">
                  {t('footer.termsOfUse') || 'Conditions d\'utilisation'}
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-8 pt-8">
          <div className="text-center">
            <p className="text-gray-400 text-sm">
              © 2024/{currentYear} OpSecours - Tous droits réservés
            </p>
            <div className="flex items-center justify-center gap-2 mt-3">
              <span className="text-gray-500 text-xs">Développé par</span>
            {/*
              <img 
                src="" 
                alt="" 
                className="h-5 w-auto opacity-80 hover:opacity-100 transition-opacity" 
              />  */}
              <span className="text-gray-400 text-xs font-medium">Bondi51 & Team OpSecours</span>
            </div>
            <p className="text-gray-500 text-xs mt-2">
              {t('footer.beta')}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;

